import {
  Table, Badge, Group, Button, Modal, TextInput, ActionIcon, Tooltip,
  Alert, Skeleton, Text, Stack, Tabs, Code, ScrollArea, PasswordInput,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import {
  IconPlus, IconLock, IconLockOpen, IconKey, IconTrash, IconTerminal2,
  IconBrandOpenai, IconAlertTriangle,
} from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ldapApi, type LdapPerson } from '@/api/ldap'
import { formatApiError } from '@/api/errors'

export function PessoasTab() {
  const qc = useQueryClient()

  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false)
  const [detailPerson, setDetailPerson] = useState<LdapPerson | null>(null)
  const [resetTokenModal, setResetTokenModal] = useState<{ username: string; token: string } | null>(null)

  // Create form state
  const [newUsername, setNewUsername] = useState('')
  const [newDisplayName, setNewDisplayName] = useState('')
  const [newEmail, setNewEmail] = useState('')

  // SSH key form state
  const [sshTag, setSshTag] = useState('')
  const [sshKey, setSshKey] = useState('')

  // Unix cred form
  const [unixPassword, setUnixPassword] = useState('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['ldap-persons'],
    queryFn: () => ldapApi.listPersons(),
  })

  const { data: personDetail, isLoading: detailLoading } = useQuery({
    queryKey: ['ldap-person', detailPerson?.username],
    queryFn: () => ldapApi.getPerson(detailPerson!.username),
    enabled: !!detailPerson,
  })

  const { data: sshKeys } = useQuery({
    queryKey: ['ldap-ssh-keys', detailPerson?.username],
    queryFn: () => ldapApi.listSshKeys(detailPerson!.username),
    enabled: !!detailPerson,
  })

  const { data: unixAttrs } = useQuery({
    queryKey: ['ldap-unix', detailPerson?.username],
    queryFn: () => ldapApi.getUnixAttrs(detailPerson!.username),
    enabled: !!detailPerson,
  })

  const createMutation = useMutation({
    mutationFn: () =>
      ldapApi.createPerson({ username: newUsername, display_name: newDisplayName, email: newEmail }),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['ldap-persons'] })
      closeCreate()
      setNewUsername('')
      setNewDisplayName('')
      setNewEmail('')
      setResetTokenModal({ username: result.person?.username ?? newUsername, token: result.reset_token })
    },
    onError: (err: any) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao criar usuário.') })
    },
  })

  const lockMutation = useMutation({
    mutationFn: (username: string) => ldapApi.lockPerson(username),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ldap-persons'] })
      notifications.show({ color: 'orange', title: 'Bloqueado', message: 'Usuário bloqueado no diretório.' })
    },
    onError: (err: any) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao bloquear.') })
    },
  })

  const unlockMutation = useMutation({
    mutationFn: (username: string) => ldapApi.unlockPerson(username),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ldap-persons'] })
      notifications.show({ color: 'teal', title: 'Desbloqueado', message: 'Usuário desbloqueado no diretório.' })
    },
    onError: (err: any) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao desbloquear.') })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (username: string) => ldapApi.deletePerson(username),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ldap-persons'] })
      notifications.show({ color: 'red', title: 'Removido', message: 'Usuário removido do diretório.' })
    },
    onError: (err: any) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao remover.') })
    },
  })

  const resetMutation = useMutation({
    mutationFn: (username: string) => ldapApi.resetPassword(username),
    onSuccess: (data, username) => {
      setResetTokenModal({ username, token: data.reset_token })
    },
    onError: (err: any) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao resetar senha.') })
    },
  })

  const addSshKeyMutation = useMutation({
    mutationFn: ({ username, tag, key }: { username: string; tag: string; key: string }) =>
      ldapApi.addSshKey(username, tag, key),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ldap-ssh-keys', detailPerson?.username] })
      setSshTag('')
      setSshKey('')
      notifications.show({ color: 'teal', title: 'SSH Key adicionada', message: '' })
    },
    onError: (err: any) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao adicionar SSH key.') })
    },
  })

  const removeSshKeyMutation = useMutation({
    mutationFn: ({ username, tag }: { username: string; tag: string }) =>
      ldapApi.removeSshKey(username, tag),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ldap-ssh-keys', detailPerson?.username] })
      notifications.show({ color: 'orange', title: 'SSH Key removida', message: '' })
    },
  })

  const setUnixCredMutation = useMutation({
    mutationFn: ({ username, password }: { username: string; password: string }) =>
      ldapApi.setUnixCredential(username, password),
    onSuccess: () => {
      setUnixPassword('')
      notifications.show({ color: 'teal', title: 'Credencial UNIX definida', message: '' })
    },
    onError: (err: any) => {
      notifications.show({ color: 'red', title: 'Erro', message: formatApiError(err, 'Falha ao definir credencial UNIX.') })
    },
  })

  if (isLoading) return <Skeleton height={200} />
  if (error) return <Alert color="red" icon={<IconAlertTriangle />}>Falha ao carregar persons do Kanidm.</Alert>

  const persons = data?.items ?? []

  return (
    <>
      <Group justify="flex-end" mb="sm">
        <Button leftSection={<IconPlus size={14} />} size="xs" onClick={openCreate}>
          Novo Usuário
        </Button>
      </Group>

      <ScrollArea>
        <Table striped highlightOnHover withTableBorder fz="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Username</Table.Th>
              <Table.Th>Nome</Table.Th>
              <Table.Th>Email</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Ações</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {persons.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text c="dimmed" ta="center" size="sm">Nenhum usuário encontrado.</Text>
                </Table.Td>
              </Table.Tr>
            )}
            {persons.map((p) => (
              <Table.Tr key={p.username}>
                <Table.Td>
                  <Text
                    size="sm"
                    fw={500}
                    style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                    onClick={() => setDetailPerson(p)}
                  >
                    {p.username}
                  </Text>
                </Table.Td>
                <Table.Td>{p.display_name}</Table.Td>
                <Table.Td>{p.email}</Table.Td>
                <Table.Td>
                  <Badge color={p.is_locked ? 'red' : 'teal'} size="xs">
                    {p.is_locked ? 'Bloqueado' : 'Ativo'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    <Tooltip label={p.is_locked ? 'Desbloquear' : 'Bloquear'}>
                      <ActionIcon
                        size="xs"
                        variant="subtle"
                        color={p.is_locked ? 'teal' : 'orange'}
                        onClick={() =>
                          p.is_locked
                            ? unlockMutation.mutate(p.username)
                            : lockMutation.mutate(p.username)
                        }
                      >
                        {p.is_locked ? <IconLockOpen size={13} /> : <IconLock size={13} />}
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Reset de senha">
                      <ActionIcon
                        size="xs"
                        variant="subtle"
                        color="blue"
                        onClick={() => resetMutation.mutate(p.username)}
                      >
                        <IconKey size={13} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Detalhes">
                      <ActionIcon
                        size="xs"
                        variant="subtle"
                        color="gray"
                        onClick={() => setDetailPerson(p)}
                      >
                        <IconTerminal2 size={13} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Remover">
                      <ActionIcon
                        size="xs"
                        variant="subtle"
                        color="red"
                        onClick={() => {
                          if (confirm(`Remover ${p.username} do diretório?`))
                            deleteMutation.mutate(p.username)
                        }}
                      >
                        <IconTrash size={13} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>

      {/* Modal: Criar usuário */}
      <Modal opened={createOpened} onClose={closeCreate} title="Novo Usuário no Kanidm" size="sm">
        <Stack gap="sm">
          <TextInput
            label="Username"
            placeholder="joao.silva"
            value={newUsername}
            onChange={(e) => setNewUsername(e.target.value)}
            required
          />
          <TextInput
            label="Nome completo"
            placeholder="João Silva"
            value={newDisplayName}
            onChange={(e) => setNewDisplayName(e.target.value)}
            required
          />
          <TextInput
            label="Email"
            placeholder="joao.silva@empresa.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            required
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeCreate}>Cancelar</Button>
            <Button
              onClick={() => createMutation.mutate()}
              loading={createMutation.isPending}
              disabled={!newUsername || !newDisplayName || !newEmail}
            >
              Criar
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal: Reset token */}
      <Modal
        opened={!!resetTokenModal}
        onClose={() => setResetTokenModal(null)}
        title="Token de Reset de Senha"
        size="md"
      >
        {resetTokenModal && (
          <Stack gap="sm">
            <Alert color="blue" icon={<IconKey />}>
              Token gerado para <strong>{resetTokenModal.username}</strong>. Envie ao usuário para que ele defina sua senha.
            </Alert>
            <Text size="sm" fw={500}>Token / URL:</Text>
            <Code block style={{ wordBreak: 'break-all' }}>{resetTokenModal.token}</Code>
            <Button variant="subtle" onClick={() => setResetTokenModal(null)}>Fechar</Button>
          </Stack>
        )}
      </Modal>

      {/* Modal: Detalhes do person */}
      <Modal
        opened={!!detailPerson}
        onClose={() => setDetailPerson(null)}
        title={`Detalhes: ${detailPerson?.username}`}
        size="lg"
      >
        {detailPerson && (
          <Tabs defaultValue="info">
            <Tabs.List>
              <Tabs.Tab value="info">Info</Tabs.Tab>
              <Tabs.Tab value="credentials">Credenciais</Tabs.Tab>
              <Tabs.Tab value="ssh">SSH Keys</Tabs.Tab>
              <Tabs.Tab value="unix">UNIX</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="info" pt="sm">
              {detailLoading ? <Skeleton height={100} /> : (
                <Stack gap="xs">
                  <Text size="sm"><strong>Username:</strong> {personDetail?.username}</Text>
                  <Text size="sm"><strong>Nome:</strong> {personDetail?.display_name}</Text>
                  <Text size="sm"><strong>Email:</strong> {personDetail?.email}</Text>
                  <Text size="sm"><strong>SPN:</strong> {personDetail?.spn}</Text>
                  <Text size="sm"><strong>Status:</strong> {personDetail?.is_locked ? 'Bloqueado' : 'Ativo'}</Text>
                  <Text size="sm"><strong>Grupos:</strong></Text>
                  {(personDetail?.groups ?? []).map((g) => (
                    <Badge key={g} size="xs" variant="outline">{g}</Badge>
                  ))}
                </Stack>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="credentials" pt="sm">
              {detailLoading ? <Skeleton height={80} /> : (
                <Stack gap="xs">
                  <Group gap="sm">
                    <Badge
                      color={personDetail?.credential_status?.has_primary == null
                        ? 'gray'
                        : (personDetail.credential_status.has_primary ? 'teal' : 'orange')}
                      size="sm"
                    >
                      {personDetail?.credential_status?.has_primary == null
                        ? 'Senha primária: não foi possível verificar'
                        : (personDetail.credential_status.has_primary
                          ? 'Senha primária: OK' : 'Sem senha primária')}
                    </Badge>
                    {/* O status do Kanidm não expõe a credencial unix: quem autentica
                        por LDAP simple bind vem sem nenhum sinal aqui. Dizer "Sem UNIX
                        cred" afirmava ausência que não se pode afirmar. */}
                    <Badge color="gray" size="sm">
                      {personDetail?.credential_status?.has_unix == null
                        ? 'UNIX cred: não informado pelo diretório'
                        : (personDetail.credential_status.has_unix
                          ? 'UNIX cred: OK' : 'Sem UNIX cred')}
                    </Badge>
                    <Badge color={personDetail?.credential_status?.has_passkey ? 'blue' : 'gray'} size="sm">
                      {personDetail?.credential_status?.has_passkey ? 'Passkey: OK' : 'Sem passkey'}
                    </Badge>
                  </Group>
                  <Button
                    size="xs"
                    variant="outline"
                    leftSection={<IconKey size={13} />}
                    onClick={() => resetMutation.mutate(detailPerson.username)}
                    loading={resetMutation.isPending}
                  >
                    Gerar token de reset
                  </Button>
                </Stack>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="ssh" pt="sm">
              <Stack gap="sm">
                <Text size="sm" fw={500}>SSH Keys cadastradas:</Text>
                {(sshKeys?.items ?? []).length === 0 && (
                  <Text c="dimmed" size="sm">Nenhuma SSH key cadastrada.</Text>
                )}
                {(sshKeys?.items ?? []).map((k) => (
                  <Group key={k.tag} justify="space-between" wrap="nowrap">
                    <Text size="xs" fw={500}>{k.tag}</Text>
                    <Group gap={4}>
                      <Code style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 10 }}>
                        {k.key.substring(0, 40)}...
                      </Code>
                      <ActionIcon
                        size="xs"
                        color="red"
                        variant="subtle"
                        onClick={() => removeSshKeyMutation.mutate({ username: detailPerson.username, tag: k.tag })}
                      >
                        <IconTrash size={12} />
                      </ActionIcon>
                    </Group>
                  </Group>
                ))}
                <Text size="sm" fw={500} mt="xs">Adicionar SSH Key:</Text>
                <TextInput
                  size="xs"
                  placeholder="Tag (ex: laptop-work)"
                  value={sshTag}
                  onChange={(e) => setSshTag(e.target.value)}
                />
                <TextInput
                  size="xs"
                  placeholder="ssh-ed25519 AAAA..."
                  value={sshKey}
                  onChange={(e) => setSshKey(e.target.value)}
                />
                <Button
                  size="xs"
                  disabled={!sshTag || !sshKey}
                  loading={addSshKeyMutation.isPending}
                  onClick={() => addSshKeyMutation.mutate({ username: detailPerson.username, tag: sshTag, key: sshKey })}
                >
                  Adicionar
                </Button>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="unix" pt="sm">
              <Stack gap="sm">
                <Text size="sm" fw={500}>Atributos UNIX:</Text>
                {unixAttrs ? (
                  <Stack gap="xs">
                    <Text size="sm"><strong>UID:</strong> {unixAttrs.uidnumber ?? 'N/A'}</Text>
                    <Text size="sm"><strong>GID:</strong> {unixAttrs.gidnumber ?? 'N/A'}</Text>
                    <Text size="sm"><strong>Shell:</strong> {unixAttrs.loginshell ?? 'N/A'}</Text>
                  </Stack>
                ) : (
                  <Text c="dimmed" size="sm">Atributos UNIX não configurados.</Text>
                )}
                <Text size="sm" fw={500} mt="xs">Definir credencial UNIX:</Text>
                <PasswordInput
                  size="xs"
                  placeholder="Nova senha UNIX"
                  value={unixPassword}
                  onChange={(e) => setUnixPassword(e.target.value)}
                  data-sensitive="true"
                  wrapperProps={{ 'data-sensitive': 'true' }}
                />
                <Button
                  size="xs"
                  disabled={!unixPassword}
                  loading={setUnixCredMutation.isPending}
                  onClick={() => setUnixCredMutation.mutate({ username: detailPerson.username, password: unixPassword })}
                >
                  Definir
                </Button>
              </Stack>
            </Tabs.Panel>
          </Tabs>
        )}
      </Modal>
    </>
  )
}
